import type { AppointmentStatusValue } from '@crm/shared';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { usePlatformPrefix } from '@/lib/platform-scope';
import type { PaginationMeta } from '@/modules/auth/types';

export const MANAGEMENT_ROUTES = {
  APPOINTMENTS_REPORT: '/relatorios/atendimentos',
  REVENUE_REPORT: '/relatorios/faturamento',
  CASH_FLOW: '/relatorios/dfc',
  INCOME_STATEMENT: '/relatorios/dre',
  AUDIT: '/auditoria',
} as const;

const ENDPOINT = '/api/v1';

export interface ReportFilters {
  from: Date;
  to: Date;
  unitId?: string;
  professionalId?: string;
  serviceId?: string;
}

export interface ReportRow {
  id: string;
  startAt: string;
  endAt: string;
  status: AppointmentStatusValue;
  unitName: string;
  customerName: string;
  professionalName: string | null;
  services: string;
  durationMinutes: number;
  subtotal: number;
  discountTotal: number;
  total: number;
}

export interface Kpis {
  byStatus: { status: AppointmentStatusValue; count: number }[];
  total: number;
  completed: number;
  cancelled: number;
  noShow: number;
  conversionRate: number;
  revenue: number;
  averageTicket: number;
}

export interface Breakdown {
  id: string;
  name: string;
  count: number;
  total: number;
}

export interface RevenueReport {
  total: number;
  count: number;
  averageTicket: number;
  grossTotal: number;
  discountTotal: number;
  couponTotal: number;
  cashbackTotal: number;
  netTotal: number;
  commissionTotal: number;
  sales?: ReportRow[];
  /** A resposta trouxe menos vendas do que o periodo tem. */
  truncated?: boolean;
  byProfessional?: Breakdown[];
  byService?: Breakdown[];
  byUnit?: Breakdown[];
}

export interface CashFlowMonth {
  month: number;
  income: number;
  expense: number;
  balance: number;
  realizedIncome: number;
  realizedExpense: number;
}

export interface CashFlow {
  year: number;
  months: CashFlowMonth[];
  totals: { income: number; expense: number; balance: number };
}

export interface IncomeStatement {
  from: string;
  to: string;
  grossRevenue: number;
  discounts: number;
  coupons: number;
  cashback: number;
  netRevenue: number;
  commissions: number;
  cardFees: number;
  otherIncome: number;
  expenses: { name: string; total: number }[];
  expenseTotal: number;
  result: number;
}

/** Um ponto do grafico de atendimentos realizados. */
export interface ServedPoint {
  label: string;
  count: number;
}

export interface DashboardData {
  agenda: {
    appointmentsToday: number;
    appointmentsWeek: number;
    servedToday: number;
    servedWeek: number;
  };
  leads: {
    /** Leads que entraram no funil na competencia olhada. */
    newMonth: number;
    /** Leads abertos esperando alguem do salao responder. */
    pendingFollowUps: number;
    /** Abertos, sem horario marcado e parados ha mais de 30 dias. */
    lost: number;
  };
  customers: {
    newMonth: number;
    newYear: number;
    returnedQuarter: number;
    notReturnedQuarter: number;
  };
  goals: {
    customers: number | null;
    customersProgress: number | null;
    returning: number | null;
    returningMonth: number;
    returningProgress: number | null;
    /** Quanto dos leads do mes deve virar horario marcado, em porcentagem. */
    winRateGoal: number | null;
    /** A taxa real do mes. */
    winRate: number | null;
    winRateProgress: number | null;
    /** Falso quando a empresa reservou os numeros de dinheiro ao administrador. */
    canSeeRevenue: boolean;
  };
  cashback: { earnedMonth: number; usedMonth: number; active: number };
  byMonth: {
    month: number;
    newCustomers: number;
    returningCustomers: number;
    customers: number;
  }[];
  served: { byHour: ServedPoint[]; byDay: ServedPoint[]; byMonth: ServedPoint[] };
  /** A mesma serie em dinheiro. Nulo para quem nao pode ver faturamento. */
  revenue: { byHour: ServedPoint[]; byDay: ServedPoint[]; byMonth: ServedPoint[] } | null;
}

export interface TimelineEvent {
  id: string;
  appointmentId: string;
  kind: 'created' | 'checked-in' | 'completed' | 'cancelled' | 'no-show';
  at: string;
  customerName: string;
  professionalName: string | null;
  unitName: string;
  summary: string;
}

export interface AuditEntry {
  id: string;
  userName: string;
  method: string;
  route: string;
  path: string;
  entity: string;
  entityId: string | null;
  statusCode: number;
  createdAt: string;
}

/** Monta a query dos relatorios; so entra o filtro realmente escolhido. */
export function reportParams(
  filters: ReportFilters,
  extra: Record<string, string | boolean | undefined> = {},
): string {
  const query = new URLSearchParams({
    from: filters.from.toISOString(),
    to: filters.to.toISOString(),
  });

  if (filters.unitId) query.set('unitId', filters.unitId);
  if (filters.professionalId) query.set('professionalId', filters.professionalId);
  if (filters.serviceId) query.set('serviceId', filters.serviceId);

  for (const [key, value] of Object.entries(extra)) {
    if (value === undefined || value === false) continue;
    query.set(key, String(value));
  }

  return query.toString();
}

const KEYS = { all: ['management'] as const };

export function useKpis(filters: ReportFilters) {
  const query = reportParams(filters);

  return useQuery({
    queryKey: [...KEYS.all, 'kpis', query],
    queryFn: () => apiClient.get<Kpis>(`${ENDPOINT}/reports/kpis?${query}`),
    placeholderData: (previous) => previous,
  });
}

export function useAppointmentsReport(filters: ReportFilters, status?: string) {
  const query = reportParams(filters, { status });

  return useQuery({
    queryKey: [...KEYS.all, 'appointments', query],
    queryFn: () =>
      apiClient.get<{
        data: ReportRow[];
        truncated: boolean;
        totals: { count: number; total: number };
      }>(`${ENDPOINT}/reports/appointments?${query}`),
    placeholderData: (previous) => previous,
  });
}

export function useRevenueReport(
  filters: ReportFilters,
  flags: {
    includeSales?: boolean;
    byProfessional?: boolean;
    byService?: boolean;
    byUnit?: boolean;
  },
) {
  const prefix = usePlatformPrefix();
  const query = reportParams(filters, flags);

  return useQuery({
    queryKey: [...KEYS.all, prefix, 'revenue', query],
    queryFn: () => apiClient.get<RevenueReport>(`/api/v1${prefix}/reports/revenue?${query}`),
    placeholderData: (previous) => previous,
  });
}

export function useCashFlow(year: number, unitId?: string) {
  const prefix = usePlatformPrefix();
  const query = new URLSearchParams({ year: String(year) });
  if (unitId) query.set('unitId', unitId);

  return useQuery({
    queryKey: [...KEYS.all, prefix, 'cash-flow', query.toString()],
    queryFn: () => apiClient.get<CashFlow>(`/api/v1${prefix}/reports/cash-flow?${query}`),
    placeholderData: (previous) => previous,
  });
}

export function useIncomeStatement(filters: ReportFilters) {
  const prefix = usePlatformPrefix();
  const query = reportParams({ from: filters.from, to: filters.to, unitId: filters.unitId });

  return useQuery({
    queryKey: [...KEYS.all, prefix, 'income-statement', query],
    queryFn: () =>
      apiClient.get<IncomeStatement>(`/api/v1${prefix}/reports/income-statement?${query}`),
    placeholderData: (previous) => previous,
  });
}

export function useDashboard(params: { unitId?: string; year?: number; month?: number } = {}) {
  const query = new URLSearchParams();
  if (params.unitId) query.set('unitId', params.unitId);
  if (params.year) query.set('year', String(params.year));
  if (params.month) query.set('month', String(params.month));

  return useQuery({
    queryKey: [...KEYS.all, 'dashboard', query.toString()],
    queryFn: () => apiClient.get<DashboardData>(`${ENDPOINT}/reports/dashboard?${query}`),
    placeholderData: (previous) => previous,
  });
}

export function useTimeline(limit = 15, unitId?: string) {
  const query = new URLSearchParams({ limit: String(limit) });
  if (unitId) query.set('unitId', unitId);

  return useQuery({
    queryKey: [...KEYS.all, 'timeline', query.toString()],
    queryFn: () => apiClient.get<{ data: TimelineEvent[] }>(`${ENDPOINT}/timeline?${query}`),
  });
}

export function useAudit(page: number, entity?: string, perPage = 20) {
  const query = new URLSearchParams({ page: String(page), perPage: String(perPage) });
  if (entity) query.set('entity', entity);

  return useQuery({
    queryKey: [...KEYS.all, 'audit', query.toString()],
    queryFn: () =>
      apiClient.get<{
        data: AuditEntry[];
        meta: PaginationMeta;
      }>(`${ENDPOINT}/audit?${query}`),
    placeholderData: (previous) => previous,
  });
}
